import React, { ChangeEvent, ClipboardEvent, useCallback, useMemo, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { useForm, z, zodResolver } from '@fiverr-private/forms';
import { Link } from '@fiverr-private/typography';
import { stats } from '@fiverr-private/obs';
import pathfinder from '@fiverr-private/pathfinder';
import { raiseToast } from '@fiverr-private/toast';
import { Brief } from '@fiverr-private/ai_brief';
import { TranslateOptionsWithTemplatesParams } from '@fiverr-private/i18n-react';
import { useBriefCreationContext } from '../context/BriefCreationContext';
import { appTranslate, GenerationError, InvalidIntent } from '../i18n';
import { IntentClassification } from '../api';
import { getUserIsActive } from '../api/buyerData';
import { METRICS_PREFIX, Step } from '../constants';
import { briefCreationClickAction, briefInputPastedAction, mixpanelEvents } from '../monitoring';
import { logger } from '../utils/logger';
import { useCustomSections } from '../components';
import { BriefsMobileBridge } from '../events/BriefsMobileBridge';
import { useClassifyLeafCategory } from './classification';
import { usePopulateBuyerType } from './classification/usePopulateBuyerType';
import { isInvalidIntent } from './classification/utils';
import { useGeneratePersonBrief } from './useGeneratePersonBrief';
import { useExtractPersonBriefAdditionalData } from './useExtractPersonBriefAdditionalData';

export const MAX_INPUT_LENGTH = 2000;
export const TEXTAREA_COUNTER_THRESHOLD = 1800;

const extractAdditionalDataRequestType = 'extract_additional_data';

const mapErrorToLink: Record<InvalidIntent, string | { tos: string; cs: string }> = {
  non_english: '',
  non_service: '',
  insufficient_information: '',
  contact_support: pathfinder('help_center_homepage'),
  seller_intent: pathfinder('help_center_section', {
    section_id: '17142926163729-Become-a-seller',
  }),
  tos_violation: {
    tos: pathfinder('legal_portal_article_page', {
      category: 'legal-terms',
      page_slug: 'terms-of-service',
    }),
    cs: pathfinder('community_standards', { slug: 'integrity-authenticity' }),
  },
};

const getErrorTemplate = (errorMessageKey: GenerationError) => {
  const educationLink = mapErrorToLink[errorMessageKey];

  if (typeof educationLink === 'string') {
    return {
      link: (text) => (
        <Link color="red_1000" href={educationLink}>
          {text}
        </Link>
      ),
    };
  }

  return Object.keys(educationLink).reduce((acc, key) => {
    acc[key] = (text) => (
      <Link color="red_1000" href={educationLink[key]}>
        {text}
      </Link>
    );
    return acc;
  }, {});
};

const isIntent = (str: string): str is IntentClassification =>
  (Object.keys(mapErrorToLink) as IntentClassification[]).includes(str as IntentClassification);

export const usePersonBriefForm = () => {
  const {
    contextId,
    setIsOpen,
    placeholder,
    userInput,
    setUserInput,
    source,
    setStep,
    parentElement,
    setBrief,
    variant,
    createAbortController,
  } = useBriefCreationContext();
  const { generatePersonBrief, isLoading: isLoadingGenerate } = useGeneratePersonBrief();
  const { classifyLeafCategory, isLoading: isLoadingClassification } = useClassifyLeafCategory();
  const { extractAdditionalData, isLoading: isLoadingAdditionalData } = useExtractPersonBriefAdditionalData();
  const { populateBuyerType, isBuyerTypeLoading } = usePopulateBuyerType();
  const { addCustomSections } = useCustomSections();
  const [pastedInput, setPastedInput] = useState<string | null>(null);

  const schema = z.object({
    leaf_category_id: z.string().optional(),
    prompt: z
      .string()
      .min(10, appTranslate('generation_errors.minimum_10_characters') as string)
      .refine(
        (value) => {
          const words = value.trim().split(/\s+/);
          return words.length >= 5;
        },
        {
          message: appTranslate('generation_errors.minimum_5_words') as string,
        }
      ),
  });

  const methods = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      prompt: userInput,
    },
  });

  useEffect(() => {
    methods.setValue('prompt', userInput);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInput]);

  const handleIntentClassificationError = (intentClassification?: IntentClassification) => {
    if (!intentClassification) {
      return;
    }

    let errorMessage = '';

    if (isIntent(intentClassification)) {
      const errorMessageKey: GenerationError = intentClassification as unknown as GenerationError;
      if (errorMessageKey === 'insufficient_information') {
        setStep(Step.Rephrase);
        setIsOpen(true);
        return;
      }

      errorMessage = appTranslate(`generation_errors.${errorMessageKey}`, {
        templates: getErrorTemplate(errorMessageKey),
      } as TranslateOptionsWithTemplatesParams);
    } else {
      errorMessage = appTranslate('generation_errors.insufficient_information');
    }

    methods.setError('prompt', {
      type: 'manual',
      message: errorMessage as any as string,
    });
    stats.count(`${METRICS_PREFIX}.classify_user_input`, 'invalid_intent');
    logger.warn(new Error('Invalid intent'), { intentClassification, userInput });
    return errorMessage;
  };

  const validateUserNotRestricted = async () => {
    try {
      const { data: isActive } = await getUserIsActive();

      if (!isActive) {
        raiseToast({
          type: 'error',
          location: 'top',
          message: appTranslate('restricted_account_error', {
            templates: {
              bold: (text) => <b>{text}</b>,
              link: (text) => (
                <Link fontStyle="italic" href={pathfinder('help_center_homepage')}>
                  {text}
                </Link>
              ),
            },
          }),
        });

        return false;
      }
      return true;
    } catch (error) {
      logger.error(new Error('An error occurred'), { cause: error });

      raiseToast({
        type: 'error',
        location: 'top',
        message: appTranslate('something_went_wrong'),
      });
      return false;
    }
  };

  const debouncedValidate = useMemo(
    () =>
      debounce(async () => {
        await methods.trigger('prompt');
      }, 1500),
    [methods]
  );

  const setExceedsLimitError = useCallback(() => {
    debouncedValidate.cancel();
    methods.setError('prompt', {
      type: 'manual',
      message: appTranslate('generation_errors.exceeds_character_limit') as string,
    });
  }, [debouncedValidate, methods]);

  const onTextareaChange = async (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUserInput(value);
    if (pastedInput && value.length === MAX_INPUT_LENGTH && value.length - userInput.length < pastedInput.length) {
      setPastedInput(null);
      setExceedsLimitError();
    } else if (methods.formState.errors.prompt) {
      await debouncedValidate.flush();
      await methods.trigger();
    } else {
      await debouncedValidate();
    }
  };

  const handleContentPaste = async (e: ClipboardEvent) => {
    const pastedInput = e.clipboardData?.getData('text');
    setPastedInput(pastedInput);
    briefInputPastedAction(contextId, pastedInput.length);
  };

  const onSubmit = async () =>
    methods.handleSubmit(async (data) => {
      await debouncedValidate.flush();
      if (!data?.prompt) {
        return;
      }

      const abortController = createAbortController();

      briefCreationClickAction({
        step: Step.Creation,
        action: 'generate_brief',
        input: data.prompt,
        briefContextId: contextId,
        eventSource: source || '',
        parentElement,
        variant,
      });

      const isValid = await validateUserNotRestricted();
      if (!isValid) {
        return;
      }

      const onIntentClassified = async (intentClassification: IntentClassification) => {
        if (isInvalidIntent(intentClassification)) {
          handleIntentClassificationError(intentClassification);
        } else {
          const leafClassificationResult = await classifyLeafCategory({
            userInput: data.prompt,
            abortController,
          });

          if (!leafClassificationResult) {
            raiseToast({
              type: 'error',
              location: 'top',
              message: appTranslate('something_went_wrong'),
            });
            return;
          }

          const subcategoryId = leafClassificationResult.leaf_classification[0].sub_category_id;
          if (!subcategoryId) {
            logger.warn(new Error('Failed to classify subcategory id for input'), { input: data?.prompt });
            return;
          }

          return extractAdditionalData({
            leafClassification: leafClassificationResult.leaf_classification[0],
            contextId,
            userInput: data.prompt,
            abortController,
            requestType: extractAdditionalDataRequestType,
          });
        }
      };

      let extractedDataResult: any = null;
      let generatedBriefResult: any = null;
      let isExtractionComplete = false;

      const fullGenerationFlowPromise = new Promise<void>((resolve) => {
        const wrappedOnIntentClassified = async (intentClassification: IntentClassification) => {
          try {
            extractedDataResult = await onIntentClassified(intentClassification);
          } catch (error: any) {
            mixpanelEvents.seamless(variant).generateBrief.submit('Failed');
            if (!abortController.signal.aborted) {
              logger.error('Failed to execute onIntentClassified', error as Error);
            } else {
              // Handle timeout or other errors - extraction is still complete
              logger.error('Additional data extraction failed/timed out', error as Error);
            }
          } finally {
            isExtractionComplete = true;

            if (generatedBriefResult) {
              resolve();
            }
          }
        };

        generatePersonBrief({
          userInput: data.prompt,
          onIntentClassified: wrappedOnIntentClassified,
          abortController,
        })
          .then((generatedBrief) => {
            generatedBriefResult = generatedBrief;

            if (isExtractionComplete) {
              resolve();
            }
          })
          .catch((error) => {
            logger.error('Failed to generate brief', error);
            resolve();
          });
      });

      await Promise.all([fullGenerationFlowPromise, populateBuyerType(abortController)]);

      if (isExtractionComplete && generatedBriefResult) {
        const brief = addCustomSections({ brief: generatedBriefResult, additionalData: extractedDataResult });
        setBrief((prevBrief: Brief | null) =>
          prevBrief ? { ...prevBrief, ...(brief?.sections ? { sections: brief.sections } : {}) } : brief || null
        );

        BriefsMobileBridge.onBriefGenerated({
          userPrompt: methods.getValues('prompt'),
        });

        mixpanelEvents.seamless(variant).generateBrief.submit('Success');
        setStep(Step.Edit);
        setIsOpen(true);
      }
    })();

  const handlePromptKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (userInput.length === MAX_INPUT_LENGTH && !['Backspace', 'Delete'].includes(event.key)) {
      setExceedsLimitError();
    }
  };

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      onSubmit();
    }
  };

  return {
    variant,
    methods,
    onTextareaChange,
    onSubmit,
    isLoading: isLoadingClassification || isLoadingGenerate || isLoadingAdditionalData || isBuyerTypeLoading,
    placeholder,
    handleOnKeyDown,
    handleContentPaste,
    handlePromptKeyDown,
  };
};
